import { Component, ElementRef, OnInit, ViewChild, inject, OnDestroy, PLATFORM_ID, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { SessionEngineService } from './services/session-engine.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { TelemetryService } from '../../services/telemetry.service';
import { XrSession, TelemetryFrame, Breadcrumb, SpatialTarget, SceneSnapshot, SceneObject, PerformanceFrame, ExceptionInfo } from '../../models/session.model';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js';
import { USDLoader } from 'three/examples/jsm/loaders/USDLoader.js';

import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartOptions, ChartType } from 'chart.js';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { ThinkingOrbComponent } from '../thinking-orb/thinking-orb.component';
import { SessionCrashPanelComponent } from './components/session-crash-panel/session-crash-panel.component';
import { SessionPlaybackControlsComponent } from './components/session-playback-controls/session-playback-controls.component';
import { SessionExceptionModalComponent } from './components/session-exception-modal/session-exception-modal.component';
import { SessionPanelToolbarComponent, PanelName } from './components/session-panel-toolbar/session-panel-toolbar.component';
import { SessionSidePanelComponent } from './components/session-side-panel/session-side-panel.component';
import { SessionObjectHeatmapPanelComponent, DebugTransform } from './components/session-object-heatmap-panel/session-object-heatmap-panel.component';
import { SessionPerformanceChartPanelComponent } from './components/session-performance-chart-panel/session-performance-chart-panel.component';
import { SessionDataService } from './services/session-data.service';
import { SessionHeatmapService } from './services/session-heatmap.service';

@Component({
  selector: 'app-session-detail',
  standalone: true,
  providers: [SessionEngineService, SessionDataService, SessionHeatmapService],
  imports: [
    CommonModule,
    FormsModule,
    BaseChartDirective,
    CdkDrag,
    CdkDragHandle,
    ThinkingOrbComponent,
    SessionCrashPanelComponent,
    SessionPlaybackControlsComponent,
    SessionExceptionModalComponent,
    SessionPanelToolbarComponent,
    SessionSidePanelComponent,
    SessionObjectHeatmapPanelComponent,
    SessionPerformanceChartPanelComponent
  ],
  templateUrl: './session-detail.component.html',
  styles: [`
    @keyframes panelIn {
      from { transform: translateX(20px); opacity: 0; }
      to { transform: translateX(0); opacity: 1; }
    }
    .animate-panel-in { animation: panelIn 0.2s ease-out; }
  `]
})
export class SessionDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  private subs: Subscription[] = [];
  @ViewChild('rendererContainer', { static: true }) rendererContainer!: ElementRef;

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  public telemetryService = inject(TelemetryService);
  private platformId = inject(PLATFORM_ID);
  private cdr = inject(ChangeDetectorRef);
  public engine = inject(SessionEngineService);
  public dataService = inject(SessionDataService);
  public heatmapService = inject(SessionHeatmapService);

  loading = true;
  errorState = false;

  session: XrSession | null = null;
  telemetryFrames: TelemetryFrame[] = [];

  // Live
  isLive = false;
  pollingInterval: any;
  lastTimestamp = 0;
  currentFrameIdx = 0;

  // Panels
  activePanel: 'overview' | 'breadcrumbs' | 'performance' | 'spatial' | 'objects' | 'network' | null = null;
  sidebarMode: 'full' | 'reduced' = 'full';

  toggleSidebarMode() {
    this.sidebarMode = this.sidebarMode === 'full' ? 'reduced' : 'full';
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('zaxis_sidebar_mode', this.sidebarMode);
    }
  }





  // Timezone logic
  get isTimezoneDifferent(): boolean {
    if (!this.session || this.session.timezoneOffset === undefined) return false;
    const localOffset = new Date().getTimezoneOffset() * -60; // in seconds
    // We consider it different if it's off by more than 1 minute
    return Math.abs(localOffset - this.session.timezoneOffset) > 60;
  }

  get deviceTimezoneStr(): string {
    if (!this.session || this.session.timezoneOffset === undefined) return 'UTC';
    const hours = Math.floor(Math.abs(this.session.timezoneOffset) / 3600);
    const sign = this.session.timezoneOffset >= 0 ? '+' : '-';
    return `UTC${sign}${hours.toString().padStart(2, '0')}`;
  }

  // Exception Modal
  selectedException: ExceptionInfo | null = null;

  showException(b: Breadcrumb) {
    if (b.exception) {
      this.selectedException = b.exception;
    }
  }

  closeException() {
    this.selectedException = null;
  }

  @ViewChild('telemetryTimeline') telemetryTimeline!: ElementRef<HTMLDivElement>;

  // Properties mapped to the heatmap service
  get heatmapStats() { return this.heatmapService.heatmapStats$.value; }
  get objectStats() { return this.heatmapService.objectStats; }

  // Edit mode and raycasting are now handled by SessionEngineService

  // Objects Stats handled by SessionHeatmapService
  async ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      const savedMode = localStorage.getItem('zaxis_sidebar_mode');
      if (savedMode === 'full' || savedMode === 'reduced') {
        this.sidebarMode = savedMode;
      }
    }

    const sessionId = this.route.snapshot.paramMap.get('id');
    if (isPlatformBrowser(this.platformId) && history.state?.session && history.state.session.telemetry?.length > 0) {
      this.setupSession(history.state.session);
      this.loading = false;
      this.checkIfSceneReady();
    } else if (sessionId) {
      if (isPlatformBrowser(this.platformId)) {
        this.telemetryService.getSession(sessionId).subscribe({
          next: (data) => {
            this.setupSession(data);
            this.loading = false;
            
            // Trigger progressive loading if telemetry is missing
            if (!data.telemetry || data.telemetry.length === 0) {
              this.dataService.pendingInitialSceneSnapshot = true;
              this.dataService.loadTelemetryProgressively(sessionId);
              this.dataService.loadBreadcrumbsProgressively(sessionId);
              this.dataService.loadSceneSnapshotsProgressively(sessionId, data.timestamp || 0);
              this.dataService.loadPerformanceProgressively(sessionId);
              this.dataService.loadNetworkRequestsProgressively(sessionId);
            } else {
              this.checkIfSceneReady();
            }
          },
          error: (err) => {
            console.error('Failed to fetch session', err);
            this.errorState = true;
            this.loading = false;
            this.cdr.detectChanges();
          }
        });
      }
    } else {
      this.router.navigate(['/dashboard']);
      return;
    }
    
    // Subscribe to Data Service
    this.subs.push(this.dataService.telemetryFrames$.subscribe(frames => {
      if (frames.length > 0) {
        this.telemetryFrames = frames;
        this.engine.updateTelemetry(frames);
        this.heatmapService.computeGlobalStats(frames, this.session?.breadcrumbs || []);
      }
    }));
    this.subs.push(this.dataService.performanceFrames$.subscribe(frames => {
      if (frames && frames.length > 0) {
        this.engine.updatePerformance(frames);
      }
    }));
    this.subs.push(this.dataService.breadcrumbs$.subscribe(crumbs => {
      if (crumbs.length > 0 && this.session) {
        this.session.breadcrumbs = crumbs;
      }
    }));
    this.subs.push(this.dataService.networkRequests$.subscribe(reqs => {
      if (reqs.length > 0 && this.session) {
        this.session.networkRequests = reqs;
      }
    }));
    this.subs.push(this.dataService.sceneSnapshot$.subscribe(snapshot => {
      if (snapshot) {
        if (this.session) this.session.sceneSnapshot = snapshot;
        this.engine.renderSceneSnapshot(snapshot);
        if (!this.dataService.pendingInitialSceneSnapshot) {
          this.checkIfSceneReady();
        }
      }
    }));
  }

  checkIfSceneReady() {
    if (this.engine.pendingModelDownloads$.value === 0) {
      this.engine.sceneReady$.next(true);
      this.cdr.detectChanges();
    } else {
      this.engine.sceneReady$.next(false);
      this.cdr.detectChanges();
    }
  };

  ngAfterViewInit() {

    this.engine.init(
      this.rendererContainer!,
      this.session!,
      this.telemetryFrames,
      this.cdr,
      this.platformId,
      this.isLive
    );

    // Subscribe to engine frame applied
    this.engine.frameApplied$.subscribe(index => {
      this.currentFrameIdx = index;
      if (this.cdr) this.cdr.detectChanges();
    });

    if (isPlatformBrowser(this.platformId)) {
      
    }
  }

  ngOnDestroy() {
    if (isPlatformBrowser(this.platformId)) {
      if (this.pollingInterval) clearInterval(this.pollingInterval);
    }
  }








  setupSession(sessionData: any) {
    this.session = sessionData;
    this.engine.updateSession(this.session!);
    
    if (this.session!.telemetry && Array.isArray(this.session!.telemetry)) {
      this.telemetryFrames = [...this.session!.telemetry].sort((a, b) => a.timestampOffsetMs - b.timestampOffsetMs);
      if (this.telemetryFrames.length > 0) {
        const first = this.telemetryFrames[0].timestampOffsetMs;
        const last = this.telemetryFrames[this.telemetryFrames.length - 1].timestampOffsetMs;
        this.engine.duration = (last - first) / 1000;

        if (this.isLive && (this.engine.duration - this.engine.currentTime) < 12) {
          this.engine.currentTime = this.engine.duration;
        }
      }
    }

    // Breadcrumbs handled by SessionEventsPanel

    // Render scene snapshot if available
    if (this.session!.sceneSnapshot) {
      this.engine.renderSceneSnapshot(Array.isArray(this.session!.sceneSnapshot) ? this.session!.sceneSnapshot[0] : this.session!.sceneSnapshot!);
      this.checkIfSceneReady();
    }

    if (isPlatformBrowser(this.platformId)) {
      this.checkLivenessAndPoll();
      if (this.telemetryFrames.length > 0) {
        // Delay to ensure layout is ready before applying the first frame or resizing
        setTimeout(() => {
          this.engine.onWindowResize();
          this.engine.applyFrame(0);
        }, 0);
      }
    }
  }





  togglePanel(panelNombre: string) {
    if (this.activePanel === panelNombre) {
      this.activePanel = null;
    } else {
      this.activePanel = panelNombre as any;
    }
  }

  checkLivenessAndPoll() {
    if (!this.session) return;
    const receivedTime = new Date(this.session.backend_received_at).getTime();
    const ageMs = Date.now() - receivedTime;
    this.isLive = !this.session.hasCrash && ageMs < 35000;

    if (this.isLive && !this.pollingInterval) {
      this.engine.playing = true;
      this.lastTimestamp = performance.now();

      this.pollingInterval = setInterval(() => {
        const sessionId = this.session!.id;
        this.telemetryService.getSession(sessionId).subscribe({
          next: (data) => {
            this.setupSession(data);
            this.dataService.loadTelemetryProgressively(sessionId);
            this.dataService.loadPerformanceProgressively(sessionId);
            this.dataService.loadBreadcrumbsProgressively(sessionId);

            const newAgeMs = Date.now() - new Date(this.session!.backend_received_at).getTime();
            if (this.session!.hasCrash || newAgeMs >= 35000) {
              this.isLive = false;
              clearInterval(this.pollingInterval);
              this.pollingInterval = null;
            }
          }
        });
      }, 10000);
    }
  }

  
  togglePlay() {
    this.engine.togglePlay();
  }

  seek(percent: number) {
    this.engine.seek(percent);
  }

  goBack() {
    this.router.navigate(['/sessions']);
  }

  focusAndSelectObject(name: string) {
    this.selectObject(name);
    this.engine.focusCameraOnObject(name);
  }
  linkModelUrl(obj: SceneObject) {
    this.dataService.linkModelUrl(this.session! as any, obj, () => {
      this.engine.renderSceneSnapshot(Array.isArray(this.session!.sceneSnapshot) ? this.session!.sceneSnapshot[0] : this.session!.sceneSnapshot!);
    });
  }

  async uploadModelFile(obj: SceneObject, event: any) {
    this.dataService.uploadModelFile(this.session! as any, obj, event.target.files?.[0], () => {
      this.engine.renderSceneSnapshot(Array.isArray(this.session!.sceneSnapshot) ? this.session!.sceneSnapshot[0] : this.session!.sceneSnapshot!);
    });
    event.target.value = '';
  }


  selectObject(name: string) {
    this.heatmapService.selectObject(name, this.session);
    this.heatmapService.buildLocalHeatmap(name, this.telemetryFrames, this.session?.breadcrumbs || [], this.session?.sceneSnapshot);
  }

  clearSelection() {
    this.engine.restoreCameraSelection();
    this.heatmapService.clearSelection();
  }

  resetCamera() {
    this.engine.resetCamera();
  }

}
